import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@material-ui/core';
import { Box, Grid } from '@mui/material';
import classNames from 'classnames';
import { SGSForm, SGSFormInput, SGSFormInputMask, SGSFormLayout } from 'components';
import { useArretTravailFormContext } from 'features/arretTravail/contexts/ArretTravailFormProvider';
import useValidationEffects from 'features/arretTravail/hooks/useValidationEffects';
import { TypeDeclaration } from 'features/arretTravail/models/typeChoix';
import { saveEtablissement } from 'features/arretTravail/services/workStoppageService';
import { useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import styles from '../../../css/ArretTravail.module.scss';
import ModalAjouterEtablissement from './ModalAddEtablissement';

type Props = {
    store: any;
    onSubmit: SubmitHandler<EtablissementSchema>;
    actions?: JSX.Element;
};

type EtablissementSchema = {
    etablissement: {
        id: string;
        socialReason: string;
        siret: string;
        site: string;
        phone: string;
        address: string;
        addressCplt: string;
        postalCode: string;
        city: string;
    };
};

// TODO :: add schema

const FormSubscriber: React.FC<Props> = ({ onSubmit, store, actions }) => {
    const { activeStep, setStepFailed } = useArretTravailFormContext();
    const { setIsModalAddEts } = useArretTravailFormContext();
    const [isEditingEtab, setIsEditingEtab] = useState(false);
    const [isEts, setIsEts] = useState(false);
    const [originalEtab, setOriginalEtab] = useState(null);
    const validationSchema: Yup.SchemaOf<EtablissementSchema> = Yup.object().shape({
        etablissement: Yup.object().shape({
            id: Yup.string(),
            socialReason: isEditingEtab ? Yup.string().required('La raison sociale est requise').max(100, 'Maximum 100 caractères') : Yup.string(),
            siret: isEditingEtab
                ? Yup.string()
                      .required('Le SIRET est requis')
                      .matches(/^\d{14}$/, 'Le SIRET doit contenir 14 chiffres')
                : Yup.string(),
            site: isEditingEtab ? Yup.string().optional().max(50, 'Maximum 50 caractères') : Yup.string(),
            phone: isEditingEtab
                ? Yup.string().optional()
                : //.matches(/^(\+33|0)[1-9](\d{2}){4}$/, 'Le numéro de téléphone est invalide') // TODO constraint
                  Yup.string(),
            address: isEditingEtab ? Yup.string().required("L'adresse est requise").max(200, 'Maximum 200 caractères') : Yup.string(),
            addressCplt: isEditingEtab ? Yup.string().optional().max(200, 'Maximum 200 caractères') : Yup.string(),
            postalCode: isEditingEtab
                ? Yup.string()
                      .required('Le code postal est requis')
                      .matches(/^\d{5}$/, 'Le code postal doit contenir 5 chiffres')
                : Yup.string(),
            city: isEditingEtab ? Yup.string().required('La ville est requise').max(100, 'Maximum 100 caractères') : Yup.string(),
        }),
    });

    const methods = useForm<EtablissementSchema>({
        resolver: yupResolver(validationSchema),
        defaultValues: store,
    });

    const {
        handleSubmit,
        formState: { errors },
        control,
        reset,
        setValue,
        clearErrors,
        trigger,
    } = methods;

    useValidationEffects({ errors, activeStep, setStepFailed, control });

    // const handleOpenModal = () => {
    //     setIsModalAddEts(true);
    // };

    const handleAddEtablissement = () => {
        setIsEditingEtab(true);
        setOriginalEtab(store.etablissement);
        reset({
            etablissement: {
                id: '',
                socialReason: '',
                siret: '',
                site: '',
                phone: '',
                address: '',
                addressCplt: '',
                postalCode: '',
                city: '',
            },
        });
    };

    const handleCancelAddEtablissement = () => {
        setValue('etablissement', { ...originalEtab });
        clearErrors();
        setIsEditingEtab(false);
    };

    const handleSelectOtherEtablissement = () => {
        setIsModalAddEts(true);
    };

    const handleSubmitEtablissement = async () => {
        try {
            const isValid = await trigger('etablissement');

            if (!isValid) {
                return;
            }
            setIsEditingEtab(false);

            const formData = methods.getValues('etablissement');

            const response = await saveEtablissement(formData);
            setValue('etablissement.id', response.id);
            setIsEts(true);
            toast.success('Etablissement enregistré avec success ');
        } catch (error) {
            setIsEditingEtab(true);
            toast.error("Une erreur est survenue durant l'enregistrement de l'établissement");
        }
    };
    const handleEtablissementSelection = async (etb) => {
        const data = {
            id: etb.id,
            socialReason: etb.name,
            siret: etb.siret,
            site: etb.site,
            phone: etb.phone,
            address: etb.address,
            addressCplt: etb.addressCplt,
            postalCode: etb.postalCod,
            city: etb.city,
        };
        setValue('etablissement', { ...data });
        setIsEts(etb.isEts === '1');
        setIsEditingEtab(false);
        const isValid = await trigger('etablissement');

        if (!isValid) {
            return;
        }
        store.etablissement = data;
    };

    return (
        <>
            <FormProvider {...methods}>
                <SGSFormLayout onSubmit={handleSubmit(onSubmit)} actions={actions}>
                    <Grid container justifyContent="center">
                        <Grid item xs={12} md={6}>
                            <Grid container spacing={1}>
                                <Grid item xs={12}>
                                    <Box className={classNames(styles.title, 'mt-4')}>Souscripteur :</Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ textAlign: 'center', fontSize: '0.9rem', fontWeight: 500 }}>{store?.subscriber?.socialReason}</Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ textAlign: 'center', fontSize: '0.9rem' }}>{store?.subscriber?.address}</Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ textAlign: 'center', fontSize: '0.9rem', fontStyle: 'italic' }}>{store?.subscriber?.addressCplt}</Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ textAlign: 'center', fontSize: '0.9rem' }}>
                                        {store?.subscriber?.postalCode} {store?.subscriber?.city}
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid container justifyContent="center">
                        <Grid item xs={12} md={6}>
                            <Box className={classNames(styles.title, 'mt-4')}>Etablissement :</Box>
                            <SGSForm.Row>
                                <SGSForm.Col md={12}>
                                    <SGSFormInput variant="outlined" name="etablissement.socialReason" label="workStop.fields.subscriber.socialReason" disabled={!isEditingEtab} />
                                </SGSForm.Col>
                            </SGSForm.Row>
                            <SGSForm.Row>
                                <SGSForm.Col md={6}>
                                    <SGSFormInput variant="outlined" name="etablissement.siret" label="workStop.fields.subscriber.siret" disabled={!isEditingEtab} />
                                </SGSForm.Col>
                                <SGSForm.Col md={6}>
                                    <SGSFormInput variant="outlined" name="etablissement.site" label="workStop.fields.subscriber.magasin" disabled={!isEditingEtab} />
                                </SGSForm.Col>
                            </SGSForm.Row>
                            <SGSForm.Row>
                                <SGSForm.Col md={6}>
                                    <SGSFormInputMask variant="outlined" label="workStop.fields.subscriber.phone" name="etablissement.phone" mask={'99.99.99.99.99'} disabled={!isEditingEtab} />
                                </SGSForm.Col>
                            </SGSForm.Row>

                            <Box className={classNames(styles.title, 'mt-4')}>ADRESSE :</Box>
                            <SGSForm.Row>
                                <SGSForm.Col md={12}>
                                    <SGSFormInput variant="outlined" name="etablissement.address" label="workStop.fields.subscriber.address" disabled={!isEditingEtab} />
                                </SGSForm.Col>
                            </SGSForm.Row>
                            <SGSForm.Row>
                                <SGSForm.Col md={12}>
                                    <SGSFormInput variant="outlined" name="etablissement.addressCplt" label="workStop.fields.subscriber.addressCplt" disabled={!isEditingEtab} />
                                </SGSForm.Col>
                            </SGSForm.Row>
                            <SGSForm.Row>
                                <SGSForm.Col md={6}>
                                    <SGSFormInput variant="outlined" name="etablissement.postalCode" label="workStop.fields.subscriber.postalCode" disabled={!isEditingEtab} />
                                </SGSForm.Col>
                                <SGSForm.Col md={6}>
                                    <SGSFormInput variant="outlined" name="etablissement.city" label="workStop.fields.subscriber.city" disabled={!isEditingEtab} />
                                </SGSForm.Col>
                            </SGSForm.Row>
                        </Grid>
                    </Grid>
                    {isEts && !isEditingEtab && (
                        <Grid container justifyContent="center">
                            <Grid item xs={12} md={6}>
                                <SGSForm.Row>
                                    <SGSForm.Col md={3}></SGSForm.Col>
                                    <SGSForm.Col md={6}>
                                        <Button variant="contained" color="primary" type="button" className={'ml-2 mr-2 mt-3'} onClick={() => setIsEditingEtab(true)}>
                                            MODIFIER
                                        </Button>
                                    </SGSForm.Col>
                                    <SGSForm.Col md={3}></SGSForm.Col>
                                </SGSForm.Row>
                            </Grid>
                        </Grid>
                    )}
                    {store?.typeDeclarationCod === TypeDeclaration.Choice2 &&
                        (!isEditingEtab ? (
                            <>
                                <Grid container justifyContent="center">
                                    <Grid item xs={12} md={6}>
                                        <SGSForm.Row>
                                            <SGSForm.Col md={6}>
                                                <Button variant="contained" color="primary" type="button" className={'ml-2 mr-2 mt-3'} onClick={handleAddEtablissement}>
                                                    Ajouter un établissement d'attache
                                                </Button>
                                            </SGSForm.Col>
                                            <SGSForm.Col md={6}>
                                                <Button variant="contained" color="primary" type="button" className={'ml-2 mr-2 mt-3'} onClick={handleSelectOtherEtablissement}>
                                                    Choisir une autre établissement
                                                </Button>
                                            </SGSForm.Col>
                                        </SGSForm.Row>
                                    </Grid>
                                </Grid>
                            </>
                        ) : (
                            <Grid container justifyContent="center">
                                <Grid item xs={12} md={6}>
                                    <SGSForm.Row>
                                        <SGSForm.Col md={6}>
                                            <Button variant="contained" color="primary" type="button" className={'ml-2 mr-2 mt-3'} onClick={handleCancelAddEtablissement}>
                                                Annuler
                                            </Button>
                                        </SGSForm.Col>
                                        <SGSForm.Col md={6}>
                                            <Button variant="contained" color="primary" type="button" className={'ml-2 mr-2 mt-3'} onClick={handleSubmitEtablissement}>
                                                ENREGISTRER
                                            </Button>
                                        </SGSForm.Col>
                                    </SGSForm.Row>
                                </Grid>
                            </Grid>
                        ))}
                </SGSFormLayout>
            </FormProvider>
            <ModalAjouterEtablissement handleEtablissementSelection={handleEtablissementSelection} siret={store?.subscriber?.siret} />
        </>
    );
};

export default FormSubscriber;
